import { useEffect, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

/**
 * Hook to track checkout progression events.
 * Best-effort (silent failures), debounced, keepalive-enabled for backend integration.
 * Restaurant ID and auth token extracted from context in integrations.
 */

const DEBOUNCE_MS = 1000;
const TRACKING_ENDPOINT = '/api/checkout-tracking/event';

export function useCheckoutTracking(restaurantId, token) {
  const checkoutIdRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const lastTrackedStageRef = useRef(null);

  // Initialize checkout_id once on mount
  useEffect(() => {
    if (!checkoutIdRef.current) {
      checkoutIdRef.current = `checkout_${uuidv4()}`;
    }
  }, []);

  const trackEvent = useCallback(
    async (eventType, metadata = {}) => {
      if (!restaurantId || !token || !checkoutIdRef.current) {
        return; // Silent fail: missing context
      }

      // Debounce rapid duplicate events
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(async () => {
        try {
          await fetch(TRACKING_ENDPOINT, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            keepalive: true,
            body: JSON.stringify({
              checkout_id: checkoutIdRef.current,
              restaurant_id: restaurantId,
              event_type: eventType,
              ...metadata,
            }),
          });
        } catch (err) {
          // Silent failure: never block checkout
          console.debug('[useCheckoutTracking]', err.message);
        }
      }, DEBOUNCE_MS);
    },
    [restaurantId, token]
  );

  return {
    trackEvent,
    checkoutId: checkoutIdRef.current,
  };
}
